import editorialStyle from "../../../components/editorial/style.scss";
import expandableStyle from "../../../components/expandable/style.scss";
import style from "./style.scss";

import { Fragment } from "preact";
import Link, {
  Button,
  ButtonList,
  ButtonBar,
  affiliateMerchants,
} from "../../../components/link";
import AffiliateDisclosure from "../../../components/affiliate-disclosure";
import Center from "../../../components/center";
import Editorial from "../../../components/editorial";
import FixedTable from "../../../components/fixed-table";
import Head from "../../../components/head";
import Hero from "../../../components/hero";
import Tags from "../../../components/tags";
import Image from "../../../components/image";
import ImageSet from "../../../components/image-set";
import Author from "../../../components/author";
import EcoFlowCouponCode from "../components/coupon-code";
import NoWrap from "../../../components/no-wrap";
import Expandable from "../../../components/expandable";
import Accordion from "../../../components/accordion";
import AccordionPreview from "../../../components/accordion/preview";
import SlugHeading from "../../../components/slug-heading";
import SlugLink from "../../../components/slug-link";
import PhotoListItem from "../../../components/photo-list-item";
import ReviewItem from "../../../components/review-item";
import Questions from "../../../components/questions";
import Warning from "../../../components/warning";
import { lookupByUrl } from "../../../settings/route-data-lookup";
import { mergeClasses } from "../../../components/helpers";
import namedRoutes from "../../../settings/routes";

import AffiliateAd from "../../../components/affiliate-ad";

import powerStationData from "../../../data/power-stations/ecoflow";
import { getGroupedData, getDataItems } from "../../power-systems/dataHelper";
import DataView from "../../power-systems/dataView";

const highlightedProducts = [
  "DELTA 3 Max Plus",
  "DELTA 3 Ultra Plus",
  "DELTA Pro",
  "DELTA Pro 3",
  "Power Kits 3.6kW (Gen 1)",
  "Power Kits 5kVA (Gen 2)",
];
const groupedData = getGroupedData(powerStationData, {
  overrideProp: "highlighted",
  overrides: highlightedProducts,
  filterFn: (item) => !item?.discontinued,
  getGroupName: (item) => {
    const dcTotal = (item.dcOutput || 0) + (item.dcOutput2 || 0);
    if (dcTotal >= 30) return "powerStationsHighDC";
    if (dcTotal > 0) return "powerStationsLowDC";
    return "powerStationsNoDC";
  },
  groups: ["powerStationsNoDC", "powerStationsLowDC", "powerStationsHighDC"],
});

const portableProducts = getDataItems(
  powerStationData,
  [
    "DELTA 3 Plus",
    "DELTA 2",
    "DELTA 3 Classic",
    "RIVER 3 Plus",
    "RIVER 2 Pro",
    "RIVER 2 Pro (700)",
  ],
  {
    overrideProp: "highlighted",
    overrides: ["DELTA 3 Plus"],
  }
);

const headerInfo = lookupByUrl(namedRoutes.ecoflow.vanlife);

const data = {
  isReleased: false,
  1000: {
    price: null,
    link: null,
  },
  600: {
    price: null,
    link: null,
  },
  xt150: {
    price: null,
    link: null,
  },
  xt60: {
    price: null,
    link: null,
  },
  plus1000Solar: {
    price: null,
    link: null,
  },
  videos: {
    showcase: null,
    install: null,
  },
};

const Page = () => (
  <div>
    <Head data={headerInfo} />
    <Hero
      positionY={50}
      background="/images/backgrounds/ecoflow-vanlife.webp"
      titlePrefix="The Ultimate Guide to"
      title="EcoFlow Power Systems"
      subTitle="For Vanlife, RVs & Overlanding"
    />
    <Editorial>
      <Center>
        <Author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />

        <p>
          <strong>
            EcoFlow power systems are a great option for vanlife, RVs, and
            overlanding.
          </strong>{" "}
          This guide covers the best EcoFlow power stations, alternator
          chargers, solar power options, and accessories, with clear
          recommendations for budgets ranging from entry‑level to high‑capacity
          off‑grid builds.
        </p>

        <AffiliateDisclosure />

        <Accordion
          items={[
            {
              heading: "Table of Contents",
              content: (
                <ol>
                  <li>
                    <SlugLink>Best EcoFlow Systems</SlugLink>
                    <ol class="upper-alpha">
                      <li>
                        <SlugLink>Top Models Comparison</SlugLink>
                      </li>
                      <li>
                        <SlugLink>Portable Companion Options</SlugLink>
                      </li>
                      <li>
                        <SlugLink>All Power Systems</SlugLink>
                      </li>
                    </ol>
                  </li>
                  <li>
                    <SlugLink>Alternator Chargers</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Solar Power</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Shore Power</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Generator Charging</SlugLink>
                  </li>
                  <li>
                    <SlugLink>Accessories</SlugLink>
                  </li>
                </ol>
              ),
            },
            {
              heading: "Terminology",
              content: (
                <Fragment>
                  <p>
                    <strong>AC power</strong> (alternating current) is the type
                    of electricity found in household outlets, allowing you to
                    run standard appliances such as a toaster, induction
                    cooktop, computer, etc.
                  </p>
                  <p>
                    <strong>Alternator</strong> is a device that powers a
                    vehicle’s electrical systems and charges the battery while
                    the engine runs.
                  </p>
                  <p>
                    <strong>Amperage</strong> (Amps, A) measures the rate of
                    electric current flow.
                  </p>
                  <p>
                    <strong>DC power</strong> (direct current) is the
                    electricity that your vehicle, EcoFlow devices, and most of
                    your essential accessories like fridges, fans, heaters, and
                    lights will use.
                  </p>
                  <p>
                    <strong>Solar array</strong> is a group of solar panels that
                    work together to convert sunlight into electricity.
                  </p>
                  <p>
                    <strong>Volts</strong> (V) measure the electric potential
                    difference between two points, indicating how strongly
                    electricity is pushed through a circuit.
                  </p>
                  <p>
                    <strong>Watts</strong> (W) are calculated by multiplying
                    volts by amps.
                  </p>
                  <p>
                    <strong>Watt-hours</strong> (Wh) and{" "}
                    <strong>kilowatt-hours</strong> (kWh) measure total energy
                    used or produced over time.
                  </p>
                </Fragment>
              ),
            },
          ]}
        />
      </Center>
      <Center>
        <SlugHeading tag="h2">Best EcoFlow Systems</SlugHeading>
        <p>
          EcoFlow power stations are a top choice for vanlife and RV electrical
          systems, reducing installation time and cost compared to DIY builds.
          From more than 25 models,{" "}
          <strong>these five deliver the best performance</strong> for off‑grid
          travel.
        </p>
        <Accordion
          right
          items={[
            {
              preview: (
                <AccordionPreview
                  headingAs="h3"
                  prefix="Most versatile"
                  heading="Delta Pro 3"
                  src="/images/products/ecoflow/delta-pro-3/front-right.webp"
                />
              ),
              content: (
                <Fragment>
                  <ImageSet
                    border={true}
                    images={[
                      {
                        lg: "/images/products/ecoflow/delta-pro-3/front.webp",
                        alt: "",
                      },
                      {
                        lg: "/images/products/ecoflow/delta-pro-3/back.webp",
                        alt: "",
                      },
                      {
                        lg: "/images/products/ecoflow/delta-pro-3/front-right.webp",
                        alt: "",
                      },
                      {
                        lg: "/images/products/ecoflow/delta-pro-3/left.webp",
                        alt: "",
                      },
                    ]}
                  />
                  <p>
                    <strong>
                      A highly capable power station with solid performance and
                      versatility.
                    </strong>{" "}
                    I would also have loved to see this have 60A DC out for
                    those with higher power needs.
                  </p>

                  <ReviewItem href={namedRoutes.ecoflow.dp3} />

                  <ButtonList size="medium">
                    <Button
                      size="medium"
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/delta-pro-3-portable-power-station?variant=41385721004105"
                      icon="EcoFlow"
                    >
                      View on EcoFlow
                    </Button>
                    <Button
                      size="medium"
                      affiliate="https://amzn.to/3MkrPnm"
                      href="https://www.amazon.com/dp/B0D14FMFZD/"
                      secondary
                      icon="Amazon"
                    >
                      View on Amazon
                    </Button>
                  </ButtonList>

                  <h4 class={style.titleBuy}>Reasons to buy</h4>
                  <ul>
                    <li>
                      <strong>Supports large series solar array</strong>{" "}
                      (30-150V⎓15A)
                    </li>
                    <li>
                      <strong>Two solar input ports</strong> (1,600W / 1,000W)
                    </li>
                    <li>
                      <strong>Large battery capacity</strong> (4,096Wh)
                    </li>
                    <li>
                      High AC output (4,000W total / 8,000W surge)
                      <ul>
                        <li>4x 20A AC plugs (NEMA 5-20R)</li>
                        <li>1x 30A AC plug (NEMA TT-30R)</li>
                        <li>1x 240V~16.7A plug (NEMA 6-20R)</li>
                        <li>1x 120V/240V~16.7A plug (NEMA L14-30R)</li>
                      </ul>
                    </li>
                    <li>12V Anderson Powerpole out (12.6V⎓30A, 378W max)</li>
                    <li>4x USB ports (2x USB A / 2x USB C)</li>
                    <li>1x DC5521 port (12.6V⎓DC 5A, 63W)</li>
                    <li>
                      Great value (compared to Power Kit and Delta Pro Ultra)
                    </li>
                    <li>Wheels and collapsible handle</li>
                    <li>EV station charging (separate accessory)</li>
                    <li>Supports control screen (separate accessory)</li>
                  </ul>
                  <h4 class={style.titleAvoid}>Reasons to avoid</h4>
                  <ul>
                    <li>
                      <strong>Expensive in comparison to Delta Pro</strong>{" "}
                      ($2,599 vs $1,499)
                    </li>
                    <li>Larger and heavier (27.3 × 16.2 × 13.4" / 114lbs)</li>
                    <li>No cigarette lighter port</li>
                  </ul>

                  <ButtonList>
                    <Button
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/delta-pro-3-portable-power-station?variant=41385721004105"
                      icon="EcoFlow"
                    >
                      Buy from EcoFlow
                    </Button>
                    <Button
                      affiliate="https://amzn.to/3MkrPnm"
                      href="https://www.amazon.com/dp/B0D14FMFZD/"
                      secondary
                      icon="Amazon"
                    >
                      Buy on Amazon
                    </Button>
                    <Button
                      href={namedRoutes.ecoflow.dp3}
                      secondary
                      icon="Matanich"
                    >
                      Read My Review
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
            {
              preview: (
                <AccordionPreview
                  headingAs="h3"
                  prefix="Best value"
                  heading="Delta Pro"
                  src="/images/products/ecoflow/delta-pro/front-right.webp"
                />
              ),
              content: (
                <Fragment>
                  <ImageSet
                    border={true}
                    images={[
                      {
                        lg: "/images/products/ecoflow/delta-pro/front.webp",
                        alt: "",
                      },
                      {
                        //https://bfasset.costco-static.com/U447IH35/as/psmb9ghk4fqp59ktq26qzrm/1600557-847__4?auto=webp&amp;format=jpg&width=1200&height=1200&fit=bounds&canvas=1200,1200
                        lg: "/images/products/ecoflow/delta-pro/back.webp",
                        alt: "",
                      },
                      {
                        //https://bfasset.costco-static.com/U447IH35/as/cvf7c5r597t965ncc5qrtgn/1600557-847__3?auto=webp&amp;format=jpg&width=1200&height=1200&fit=bounds&canvas=1200,1200
                        lg: "/images/products/ecoflow/delta-pro/right.webp",
                        alt: "",
                      },
                      {
                        lg: "/images/products/ecoflow/delta-pro/left.webp",
                        alt: "",
                      },
                    ]}
                  />
                  <p>
                    <strong>
                      Incredible value for such a capable and versatile power
                      station.
                    </strong>{" "}
                    I use this one personally on all my adventures.
                  </p>

                  <ReviewItem href={namedRoutes.ecoflow.dp} />

                  <ButtonList size="medium">
                    <Button
                      size="medium"
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/collections/delta-pro-series/products/delta-pro-portable-power-station"
                      icon="EcoFlow"
                    >
                      View on EcoFlow
                    </Button>
                    <Button
                      size="medium"
                      affiliate="https://amzn.to/4oJeF0L"
                      href="https://www.amazon.com/dp/B0C1Z4GLKS/"
                      secondary
                      icon="Amazon"
                    >
                      View on Amazon
                    </Button>
                  </ButtonList>

                  <h4 class={style.titleBuy}>Reasons to buy</h4>
                  <ul>
                    <li>
                      <strong>Amazing value</strong> ($1,499)
                    </li>
                    <li>
                      <strong>Supports large series solar array</strong>{" "}
                      (11-150V⎓15A)
                    </li>
                    <li>Large battery capacity (3,600Wh)</li>
                    <li>
                      Great AC output (3,600W total / 7,200W surge)
                      <ul>
                        <li>4x 20A AC plugs (NEMA 5-20R)</li>
                        <li>1x 30A AC plug (TT-30R)</li>
                      </ul>
                    </li>
                    <li>12V Anderson Powerpole out (12.6V⎓30A, 378W max)</li>
                    <li>6x USB ports (4x USB A / 2x USB C)</li>
                    <li>Cigarette lighter port (8A)</li>
                    <li>2x DC5521 ports (12.6V⎓DC 3A, 38W)</li>
                    <li>Wheels and collapsible handle</li>
                    <li>EV station charging (separate accessory)</li>
                    <li>Supports control screen (separate accessory)</li>
                  </ul>
                  <h4 class={style.titleAvoid}>Reasons to avoid</h4>
                  <ul>
                    <li>
                      <strong>Single solar input port</strong>
                    </li>
                    <li>
                      <strong>Older model</strong> (released in 2021)
                    </li>
                    <li>Less capacity than Delta Pro 3 (3,600Wh vs 4,096Wh)</li>
                    <li>Larger and heavier (25 × 11.2 × 16.4" / 99lbs)</li>
                  </ul>

                  <ButtonList>
                    <Button
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/collections/delta-pro-series/products/delta-pro-portable-power-station"
                      icon="EcoFlow"
                    >
                      Buy from EcoFlow
                    </Button>
                    <Button
                      affiliate="https://amzn.to/4oJeF0L"
                      href="https://www.amazon.com/dp/B0C1Z4GLKS/"
                      secondary
                      icon="Amazon"
                    >
                      Buy on Amazon
                    </Button>
                    <Button
                      href={namedRoutes.ecoflow.dp}
                      secondary
                      icon="Matanich"
                    >
                      Read My Review
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
            {
              preview: (
                <AccordionPreview
                  headingAs="h3"
                  prefix="Well-rounded"
                  heading="Delta 3 Ultra Plus"
                  src="/images/products/ecoflow/delta-3-ultra-plus/front-right.webp"
                />
              ),
              content: (
                <Fragment>
                  <ImageSet
                    border={true}
                    images={[
                      {
                        lg: "/images/products/ecoflow/delta-3-ultra-plus/front.webp",
                        alt: "",
                      },
                      {
                        lg: "/images/products/ecoflow/delta-3-ultra-plus/back.webp",
                        alt: "",
                      },
                      {
                        lg: "/images/products/ecoflow/delta-3-ultra-plus/front-right.webp",
                        alt: "",
                      },
                      {
                        lg: "/images/products/ecoflow/delta-3-ultra-plus/right.webp",
                        alt: "",
                      },
                    ]}
                  />
                  <p>
                    <strong>
                      A capable mid-range power station with solid performance.
                    </strong>{" "}
                    However, the Delta Pro's pricing makes this model difficult
                    to justify. Had its solar input been 11-100V per port
                    instead of 11-60V, I would recommend this to many more
                    people.
                  </p>

                  <ReviewItem href={namedRoutes.ecoflow.d3ultraPlus} />

                  <ButtonList size="medium">
                    <Button
                      size="medium"
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/delta-3-ultra-series-portable-power-station?variant=54718705008713&view=d3up"
                      icon="EcoFlow"
                    >
                      View on EcoFlow
                    </Button>
                    <Button
                      size="medium"
                      affiliate="https://amzn.to/4svqHOK"
                      href="https://www.amazon.com/dp/B0FQVG59LV/"
                      secondary
                      icon="Amazon"
                    >
                      View on Amazon
                    </Button>
                  </ButtonList>

                  <h4 class={style.titleBuy}>Reasons to buy</h4>
                  <ul>
                    <li>
                      Great AC output (3,600W total / 7,200W surge)
                      <ul>
                        <li>4x 20A AC plugs (NEMA 5-20R)</li>
                        <li>1x 30A AC plug (TT-30R)</li>
                      </ul>
                    </li>
                    <li>Two solar input ports (800W each)</li>
                    <li>Moderate battery capacity (3,072Wh)</li>
                    <li>
                      Not too big or too heavy (24.1 × 12.9 × 15.6" / 74lbs)
                    </li>
                    <li>12V Anderson Powerpole out (12.6V⎓30A, 378W max)</li>
                    <li>Cigarette lighter port (10A)</li>
                    <li>4x USB ports (1x USB A / 3x USB C)</li>
                    <li>Wheels and collapsible handle</li>
                  </ul>
                  <h4 class={style.titleAvoid}>Reasons to avoid</h4>
                  <ul>
                    <li>
                      <strong>
                        {/* Reasonable price but more expensive than Delta Pro */}
                        {/* Great price compared to Delta Pro */}
                        Great price, similar to Delta Pro
                      </strong>{" "}
                      ($1,499 vs $1,499)
                    </li>
                    <li>
                      <strong>
                        Doesn't support several solar panels in series
                      </strong>{" "}
                      (60V⎓ max)
                    </li>
                    <li>
                      Requires{" "}
                      <Link
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/ecoflow-extra-battery-expansion-adapter?variant=55507615645769"
                      >
                        Extra Battery Expansion Adapter
                      </Link>{" "}
                      accessory to use two extra batteries or an XT150
                      alternator charger with an extra battery.
                    </li>
                  </ul>

                  <ButtonList>
                    <Button
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/delta-3-ultra-series-portable-power-station?variant=54718705008713&view=d3up"
                      icon="EcoFlow"
                    >
                      Buy from EcoFlow
                    </Button>
                    <Button
                      affiliate="https://amzn.to/4svqHOK"
                      href="https://www.amazon.com/dp/B0FQVG59LV/"
                      secondary
                      icon="Amazon"
                    >
                      Buy on Amazon
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
            {
              preview: (
                <AccordionPreview
                  headingAs="h3"
                  prefix="Budget-friendly"
                  heading="Delta 3 Max Plus"
                  src="/images/products/ecoflow/delta-3-max-plus/front-right.webp"
                />
              ),
              content: (
                <Fragment>
                  <ImageSet
                    border={true}
                    images={[
                      {
                        lg: "/images/products/ecoflow/delta-3-max-plus/front.webp",
                        alt: "",
                      },
                      {
                        lg: "/images/products/ecoflow/delta-3-max-plus/back.webp",
                        alt: "",
                      },
                      {
                        lg: "/images/products/ecoflow/delta-3-max-plus/front-right.webp",
                        alt: "",
                      },
                      {
                        lg: "/images/products/ecoflow/delta-3-max-plus/right.webp",
                        alt: "",
                      },
                    ]}
                  />
                  <p>
                    <strong>
                      A space-saving, budget-friendly unit that delivers solid
                      performance.
                    </strong>{" "}
                    However, its 11–60V solar input range limits its
                    compatibility with many arrays. If it supported 11–80V per
                    port, I would recommend it to many more people.
                  </p>

                  <ReviewItem href={namedRoutes.ecoflow.d3maxPlus} />

                  <ButtonList size="medium">
                    <Button
                      size="medium"
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=54718699962441&view=d3mp"
                      icon="EcoFlow"
                    >
                      View on EcoFlow
                    </Button>
                    <Button
                      size="medium"
                      affiliate="https://amzn.to/4qhlyYI"
                      href="https://www.amazon.com/dp/B0FQV9Q9J2/"
                      secondary
                      icon="Amazon"
                    >
                      View on Amazon
                    </Button>
                  </ButtonList>

                  <h4 class={style.titleBuy}>Reasons to buy</h4>
                  <ul>
                    <li>
                      <strong>Budget-friendly</strong> ($1,099)
                    </li>
                    <li>
                      <strong>Small and relatively light</strong> (19.4 × 9.5 ×
                      12" / 49lbs)
                    </li>
                    <li>
                      Smallest EcoFlow power station with DC Anderson Powerpole
                      port (12.6V⎓30A, 378W max)
                    </li>
                    <li>4x 20A AC plugs (NEMA 5-20R)</li>
                    <li>Two solar input ports (500W each)</li>
                    <li>Cigarette lighter port (10A)</li>
                    <li>4x USB ports (1x USB A / 3x USB C)</li>
                  </ul>
                  <h4 class={style.titleAvoid}>Reasons to avoid</h4>
                  <ul>
                    <li>
                      <strong>
                        Doesn't support several solar panels in series
                      </strong>{" "}
                      (60V⎓ max)
                    </li>
                    <li>Smaller but reasonable battery capacity (2,048Wh)</li>
                    <li>
                      Lower but reasonable AC output (3,000W total / 6,000W
                      surge)
                    </li>
                    <li>No 30A AC plug</li>
                    <li>
                      Requires{" "}
                      <Link
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/ecoflow-extra-battery-expansion-adapter?variant=55507615645769"
                      >
                        Extra Battery Expansion Adapter
                      </Link>{" "}
                      accessory to use two extra batteries or an XT150
                      alternator charger with an extra battery.
                    </li>
                  </ul>

                  <ButtonList>
                    <Button
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=54718699962441&view=d3mp"
                      icon="EcoFlow"
                    >
                      Buy from EcoFlow
                    </Button>
                    <Button
                      affiliate="https://amzn.to/4qhlyYI"
                      href="https://www.amazon.com/dp/B0FQV9Q9J2/"
                      secondary
                      icon="Amazon"
                    >
                      Buy on Amazon
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
            {
              preview: (
                <AccordionPreview
                  headingAs="h3"
                  prefix="Highly expandable"
                  heading="Power Kits"
                  src="/images/products/ecoflow/power-kit/15kwh.webp"
                />
              ),
              content: (
                <Fragment>
                  <ImageSet
                    border={true}
                    images={[
                      {
                        lg: "/images/products/ecoflow/power-kit/power-hub.webp",
                        alt: "",
                      },
                      {
                        lg: "/images/products/ecoflow/power-kit/2kwh.webp",
                        alt: "",
                      },
                      {
                        lg: "/images/products/ecoflow/power-kit/10kwh.webp",
                        alt: "",
                      },
                      {
                        lg: "/images/products/ecoflow/power-kit/15kwh.webp",
                        alt: "",
                      },
                    ]}
                  />
                  <p>
                    <strong>
                      Designed specifically for RVs, this unit has strong
                      potential for high-demand setups.
                    </strong>{" "}
                    It’s priced similarly to the Delta Pro Ultra, but EcoFlow’s
                    decision to cap solar input at 60V per port is a major
                    limitation to an otherwise amazing product. At this price
                    point, it only makes sense when paired with several
                    batteries or when high DC output is essential.
                  </p>

                  <ButtonList size="medium">
                    <Button
                      size="medium"
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/collections/power-kits-collection"
                      icon="EcoFlow"
                    >
                      View on EcoFlow
                    </Button>
                  </ButtonList>

                  <h4 class={style.titleBuy}>Reasons to buy</h4>
                  <ul>
                    <li>
                      <strong>Very high battery capacity</strong>{" "}
                      (2,000-45,000Wh / 1-9x batteries)
                    </li>
                    <li>
                      <strong>Very high DC output</strong> (4,752W)
                    </li>
                    <li>
                      <strong>Very high DC circuits</strong> (16x controllable
                      30A circuits / 16x 50A circuits)
                    </li>
                    <li>High AC output (4,000W total / 7,500VA surge)</li>
                    <li>
                      High AC circuits (24x branch circuits / 30A max each)
                    </li>
                    <li>Three solar input ports (4,000W 1,600W each)</li>
                    <li>
                      Two models and lots of accessory options (Power Hub 5kVa &
                      Power Hub 3.6kW)
                    </li>
                    <li>Circuit panel accessory (Power Hub 5kVa support 2x)</li>
                    <li>Control screen accessory</li>
                    <li>Sensor monitoring (Power Link accessory)</li>
                  </ul>
                  <h4 class={style.titleAvoid}>Reasons to avoid</h4>
                  <ul>
                    <li>
                      <strong>
                        Doesn't support several solar panels in series
                      </strong>{" "}
                      (60V⎓ max / DP/DP3 support 150V⎓)
                    </li>
                    <li>
                      <strong>Expensive</strong> (starts at $4.7K but can
                      configure to over $40K)
                    </li>
                    <li>
                      Solar ports and alternator ports are shared (3 ports
                      total)
                    </li>
                    <li>
                      Confusing product line (so many options, needs clearer
                      guides)
                    </li>
                    <li>
                      Lack of USB ports (these can be added, but no official
                      EcoFlow accessory)
                    </li>
                    <li>
                      Lack of cigarette lighter port (these can be added, but no
                      official EcoFlow accessory)
                    </li>
                  </ul>

                  <ButtonList>
                    <Button
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/collections/power-kits-collection"
                      icon="EcoFlow"
                    >
                      Buy from EcoFlow
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
          ]}
        />

        <EcoFlowCouponCode
          href="https://us.ecoflow.com/"
          buttonText="EcoFlow Store"
        />

        <SlugHeading tag="h3">System Examples</SlugHeading>
        <ImageSet
          firstRowSize={2}
          size={3}
          images={[
            {
              lg: "/images/diagrams/power-systems/ecoflow/ecoflow-delta-pro-3.webp?v=2",
              alt: "EcoFlow DELTA Pro 3 vanlife system diagram",
            },
            {
              lg: "/images/diagrams/power-systems/ecoflow/ecoflow-power-kit-5kva.webp?v=2",
              alt: "EcoFlow Power Kit 5kVA vanlife system diagram",
            },
            {
              lg: "/images/diagrams/power-systems/ecoflow/ecoflow-delta-pro.webp?v=2",
              alt: "EcoFlow DELTA Pro vanlife system diagram",
            },
            {
              lg: "/images/diagrams/power-systems/ecoflow/ecoflow-delta-3-ultra-plus.webp?v=2",
              alt: "EcoFlow DELTA 3 Ultra Plus vanlife system diagram",
            },
            {
              lg: "/images/diagrams/power-systems/ecoflow/ecoflow-delta-3-max-plus.webp?v=3",
              alt: "EcoFlow DELTA 3 Max Plus vanlife system diagram",
            },
          ]}
        />
        <br />

        <SlugHeading tag="h3">Top Models Comparison</SlugHeading>
        <p>
          A side‑by‑side comparison of the top five EcoFlow systems, sorted by
          battery capacity:
        </p>

        <Expandable expandText="Show Hidden Rows">
          <FixedTable>
            <table>
              <thead>
                <tr>
                  <th class={editorialStyle.sticky}>Model</th>
                  <th>Delta 3 Max Plus</th>
                  <th>Delta 3 Ultra Plus</th>
                  <th>Delta Pro</th>
                  <th>Delta Pro 3</th>
                  <th>
                    Power Kits <sup>1</sup>
                  </th>
                </tr>
                <tr style="font-style:italic;">
                  <th class={editorialStyle.sticky}>AKA</th>
                  <th>D3M+</th>
                  <th>D3U+</th>
                  <th>DP</th>
                  <th>DP3</th>
                  <th>PK</th>
                </tr>
              </thead>
              <tbody>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Photo</strong>
                  </th>
                  <td>
                    <Image
                      src="/images/products/ecoflow/delta-3-max-plus/front-right.webp"
                      alt="EcoFlow Delta 3 Max Plus"
                      showAlt
                      maxHeight={100}
                    />
                  </td>
                  <td>
                    <Image
                      src="/images/products/ecoflow/delta-3-ultra-plus/front-right.webp"
                      alt="EcoFlow Delta 3 Ultra Plus"
                      showAlt
                      maxHeight={100}
                    />
                  </td>
                  <td>
                    <Image
                      src="/images/products/ecoflow/delta-pro/front-right.webp"
                      alt="EcoFlow Delta Pro"
                      showAlt
                      maxHeight={100}
                    />
                  </td>
                  <td>
                    <Image
                      src="/images/products/ecoflow/delta-pro-3/front-right.webp"
                      alt="EcoFlow Delta Pro 3"
                      showAlt
                      maxHeight={100}
                    />
                  </td>
                  <td>
                    <Image
                      src="/images/products/ecoflow/power-kit/15kwh.webp"
                      alt="EcoFlow Power Kits"
                      showAlt
                      maxHeight={100}
                    />
                  </td>
                </tr>
                <tr class={editorialStyle.noHover}>
                  <th class={editorialStyle.sticky}>
                    <strong>Links</strong>
                  </th>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/delta-3-max-series-portable-power-station?variant=54718699962441&view=d3m"
                        icon="EcoFlow"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://manuals.ecoflow.com/us/product/delta-3-max-plus?lang=en_US"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/delta-3-ultra-series-portable-power-station?variant=54718705008713&view=d3u"
                        icon="EcoFlow"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://manuals.ecoflow.com/cn/product/delta-3-ultra-plus?lang=en_US"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/delta-pro-portable-power-station?variant=40516140138569"
                        icon="EcoFlow"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://manuals.ecoflow.com/us/product/delta-pro-portable-power-station?lang=en_US"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/products/delta-pro-3-portable-power-station?variant=41385721004105"
                        icon="EcoFlow"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://manuals.ecoflow.com/us/product/delta-pro-3-portable-power-station?lang=en_US"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                  <td>
                    <ButtonBar>
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://us.ecoflow.com/collections/power-kits-collection"
                        icon="EcoFlow"
                      >
                        Store
                      </Button>
                      <Button
                        size="small"
                        external
                        href="https://manuals.ecoflow.com/us/product/power-hub-5kva?lang=en_US"
                        secondary
                      >
                        Manual
                      </Button>
                    </ButtonBar>
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Price</strong>
                  </th>
                  <td>
                    <NoWrap>
                      $1,099 <s>$1,899</s>
                    </NoWrap>
                  </td>
                  <td>
                    <NoWrap>
                      $1,499 <s>$2,899</s>
                    </NoWrap>
                  </td>
                  <td>
                    <NoWrap>
                      $1,499 <s>$3,699</s>
                    </NoWrap>
                  </td>
                  <td>
                    <NoWrap>
                      $2,599 <s>$3,699</s>
                    </NoWrap>
                  </td>
                  <td>$4.7K+</td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Capacity</strong>
                  </th>
                  <td>2,048Wh</td>
                  <td>3,072Wh</td>
                  <td>3,600Wh</td>
                  <td>4,096Wh</td>
                  <td>2-45kWh</td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Extra battery</strong>
                  </th>
                  <td>
                    2x <sup>2</sup>
                  </td>
                  <td>
                    2x <sup>2</sup>
                  </td>
                  <td>2x</td>
                  <td>2x</td>
                  <td>9x</td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>AC output</strong>
                  </th>
                  <td>
                    3,000W total
                    <br />
                    6,000W surge
                  </td>
                  <td>
                    3,600W total
                    <br />
                    7,200W surge
                  </td>
                  <td>
                    3,600W total
                    <br />
                    7,200W surge
                  </td>
                  <td>
                    4,000W total
                    <br />
                    8,000W surge
                  </td>
                  <td>
                    ≤ 4,000W
                    <br />
                    7,500VA surge
                  </td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>AC ports</strong>
                  </th>
                  <td>x4 (20A each)</td>
                  <td>
                    ×4 (20A each)
                    <br />
                    x1 (30A)
                  </td>
                  <td>
                    ×4 (20A each)
                    <br />
                    x1 (30A)
                  </td>
                  <td>
                    ×4 (20A each)
                    <br />
                    x1 (30A)
                    <br />
                    x1 (120/240V~16.7A)
                    <br />
                    x1 (240V~16.7A)
                  </td>
                  <td>≤ 24 circuits</td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>DC output</strong>
                  </th>
                  <td>12.6V⎓30A, 378W</td>
                  <td>12.6V⎓30A, 378W</td>
                  <td>12.6V⎓30A, 378W</td>
                  <td>12.6V⎓30A, 378W</td>
                  <td>≤ 4,752W</td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>
                      <NoWrap>
                        Cigarette port <sup>3</sup>
                      </NoWrap>
                    </strong>
                  </th>
                  <td>12.6V⎓10A, 126W</td>
                  <td>12.6V⎓10A, 126W</td>
                  <td>12.6V⎓8A, 100W</td>
                  <td>--</td>
                  <td>--</td>
                </tr>
                <tr class={editorialStyle.rowBorder}>
                  <th class={editorialStyle.sticky}>
                    <strong>Solar input 1</strong>
                  </th>
                  <td>
                    11-60V⎓18A
                    <br />
                    500W
                  </td>
                  <td>
                    11-60V⎓18A
                    <br />
                    800W
                  </td>
                  <td>
                    11-150V⎓15A
                    <br />
                    1,600W
                  </td>
                  <td>
                    30-150V⎓15A
                    <br />
                    1,600W
                  </td>
                  <td>
                    <NoWrap>13-60V⎓100A</NoWrap>
                    <br />≤ 4,000W
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Solar input 2</strong>
                  </th>
                  <td>
                    11V-60V⎓18A
                    <br />
                    500W
                  </td>
                  <td>
                    11-60V⎓18A
                    <br />
                    800W
                  </td>
                  <td>--</td>
                  <td>
                    11V-60V⎓20A
                    <br />
                    1,000W
                  </td>
                  <td>
                    <NoWrap>13-60V⎓30A</NoWrap>
                    <br />≤ 1,600W
                  </td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>Solar input 3</strong>
                  </th>
                  <td>--</td>
                  <td>--</td>
                  <td>--</td>
                  <td>--</td>
                  <td>
                    <NoWrap>13-60V⎓30A</NoWrap>
                    <br />≤ 1,600W
                  </td>
                </tr>
                <tr>
                  <th class={editorialStyle.sticky}>
                    <strong>Alternator input</strong>
                  </th>
                  <td>100-1,000W</td>
                  <td>100-1,000W</td>
                  <td>100-1,000W</td>
                  <td>100-2,000W</td>
                  <td>≤ 7,200W</td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>AC input</strong>
                  </th>
                  <td>15A 1,800W</td>
                  <td>15A 1,800W</td>
                  <td>15A 1,800W</td>
                  <td>15A 1,800W</td>
                  <td>≤ 4,000W</td>
                </tr>
                <tr
                  class={mergeClasses([
                    editorialStyle.rowBorder,
                    expandableStyle.expandableHide,
                  ])}
                >
                  <th class={editorialStyle.sticky}>
                    <strong>USB A</strong>
                  </th>
                  <td>1x</td>
                  <td>1x</td>
                  <td>4x</td>
                  <td>2x</td>
                  <td>0-1x</td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>USB C</strong>
                  </th>
                  <td>3x</td>
                  <td>3x</td>
                  <td>2x</td>
                  <td>2x</td>
                  <td>--</td>
                </tr>
                <tr
                  class={mergeClasses([
                    editorialStyle.rowBorder,
                    expandableStyle.expandableHide,
                  ])}
                >
                  <th class={editorialStyle.sticky}>
                    <strong>Size (in)</strong>
                  </th>
                  <td>19.4 × 9.5 × 12</td>
                  <td>24.1 × 12.9 × 15.6</td>
                  <td>25 × 11.2 × 16.4</td>
                  <td>27.3 × 16.2 × 13.4</td>
                  <td>Scalable</td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>Size (mm)</strong>
                  </th>
                  <td>
                    <NoWrap>494 × 242 × 305</NoWrap>
                  </td>
                  <td>
                    <NoWrap>613.1 × 327.7 × 395</NoWrap>
                  </td>
                  <td>
                    <NoWrap>635 × 285 × 416</NoWrap>
                  </td>
                  <td>
                    <NoWrap>693 × 410.4 × 341</NoWrap>
                  </td>
                  <td>Scalable</td>
                </tr>
                <tr class={expandableStyle.expandableHide}>
                  <th class={editorialStyle.sticky}>
                    <strong>Weight (lbs)</strong>
                  </th>
                  <td>48.7</td>
                  <td>74.3</td>
                  <td>99</td>
                  <td>113.5</td>
                  <td>Scalable</td>
                </tr>
              </tbody>
            </table>
          </FixedTable>
          <div class={expandableStyle.expandableHide}>
            <p class="caption">
              <strong>1.</strong> EcoFlow Power Kits can meet the highest
              demands, are highly expandable, and come in two versions Power Hub
              5kVa or Power Hub 3.6kW. <strong>2.</strong> Using the EcoFlow
              Extra Battery Expansion Adapter. <strong>3.</strong> DC output of
              cigarette port is included in the total overall DC output, not an
              independent value.
            </p>
          </div>
        </Expandable>
        <br />

        <AffiliateAd brand="EcoFlow" size="billboard" />

        <SlugHeading tag="h3">Portable Companion Options</SlugHeading>
        <p>
          Want to cook at a picnic table or work outside without stringing
          extension cords from your vehicle? Supplementing your main power
          system with a smaller, grab-and-go unit gives you total campsite
          freedom.
        </p>
        {/* <Warning prefix="Sale" green icon={false}>
          The{" "}
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/products/delta-3-plus-portable-power-station?variant=41826182496329"
          >
            DELTA 3 Plus
          </Link>{" "}
          is currently on sale for $599 with a free bag and is an awesome choice
          for a 1kWh power station.
        </Warning> */}
        <DataView data={portableProducts} sortMessage={null} />
        <br />

        <SlugHeading tag="h3">All Power Systems</SlugHeading>
        <p>Models are grouped by their dedicated DC output capabilities:</p>

        <Accordion
          headingsAs="h4"
          items={[
            {
              heading: "Models with 30A DC output",
              content: (
                <Fragment>
                  <DataView data={groupedData.powerStationsHighDC} />
                </Fragment>
              ),
            },
            {
              heading: "Models with 8-10A DC output",
              content: (
                <Fragment>
                  <p>
                    Best suited for single-appliance DC use or as a secondary
                    power source to your main system.
                  </p>
                  <DataView data={groupedData.powerStationsLowDC} />

                  <p>
                    I do not recommend the older Delta (1300) or Delta (1000) as
                    they use older lithium-ion batteries that wear out quickly.
                  </p>
                </Fragment>
              ),
            },
            {
              heading: "Models without dedicated DC output",
              content: (
                <Fragment>
                  <p>
                    These options fall short for typical use cases and are only
                    worth considering in edge scenarios.
                  </p>
                  <DataView data={groupedData.powerStationsNoDC} />
                </Fragment>
              ),
            },
          ]}
        />

        <p>
          <strong>See how other power systems compare:</strong>
        </p>
        <ReviewItem href={namedRoutes.powerSystems} />
      </Center>
      <Center>
        <SlugHeading tag="h2">12V Wiring</SlugHeading>
        <p>
          Learn how to safely connect 12V accessories to your EcoFlow using DC
          fuse blocks, Anderson Powerpole connectors, cigarette ports, and
          specialized adapters.
        </p>
        <ReviewItem href={namedRoutes.ecoflow.wiring12v} />
      </Center>
      <Center>
        <SlugHeading tag="h2">Alternator Chargers</SlugHeading>
        <p>
          Alternator charging is essential for vanlife, offering fast, reliable
          recharging while driving. It often outperforms solar and isn’t
          dependent on sunshine or good weather. EcoFlow currently offers two
          alternator chargers (500W and 800W)
          {/* , with{" "}
          <Link href={namedRoutes.ecoflow.altCharger.gen2}>
            600W and 1000W models
          </Link>{" "}
          expected soon */}
          . These units pull power from your vehicle to charge your power
          station, similar to a DC-DC charger.
        </p>
        <p>
          <strong>Important:</strong> Cable choice affects both power station
          compatibility and feature availability, such as reverse charging.
          Refer to the details below to compare options.
        </p>

        <Accordion
          right
          items={[
            {
              preview: (
                <AccordionPreview
                  headingAs="h3"
                  prefix="High-output"
                  heading="800W Alternator Charger"
                  src="/images/products/ecoflow/alternator-charger-800w/front-right.webp"
                />
              ),
              content: (
                <Fragment>
                  <p>
                    High-output with smart controls and flexible performance.
                  </p>

                  <ReviewItem href={namedRoutes.ecoflow.altCharger.review} />

                  <h4 class={style.titleBuy}>Reasons to buy</h4>
                  <ul>
                    <li>Includes XT150 cable</li>
                    <li>
                      Fast charge rate when using XT150 or Custom Fit cable
                      (800W)
                    </li>
                    <li>
                      Also compatible with XT60 and Custom Fit cables (purchase
                      separately)
                    </li>
                    <li>
                      Settings to lower charge rate if needed when using XT150
                      or Custom Fit cable (100-800W)
                    </li>
                  </ul>
                  <h4 class={style.titleAvoid}>Reasons to avoid</h4>
                  <ul>
                    <li>Doesn't include XT60 cable (purchase separately)</li>
                  </ul>

                  <ButtonList>
                    <Button
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/800w-alternator-charger?variant=41064248836169"
                      icon="EcoFlow"
                    >
                      Buy from EcoFlow
                    </Button>
                    <Button
                      affiliate="https://amzn.to/3N4b6p1"
                      href="https://www.amazon.com/dp/B0DSBZVPS6/"
                      secondary
                      icon="Amazon"
                    >
                      Buy on Amazon
                    </Button>
                    <Button
                      href={namedRoutes.ecoflow.altCharger.quick}
                      secondary
                      icon="Matanich"
                    >
                      Read My Review
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
            {
              preview: (
                <AccordionPreview
                  headingAs="h3"
                  prefix="Budget-friendly"
                  heading="500W Alternator Charger"
                  src="/images/products/ecoflow/alternator-charger-500w/front.webp"
                />
              ),
              content: (
                <Fragment>
                  <p>
                    Budget-friendly with solid compatibility and good
                    performance.
                  </p>

                  <ReviewItem href={namedRoutes.ecoflow.altCharger.review} />

                  <h4 class={style.titleBuy}>Reasons to buy</h4>
                  <ul>
                    <li>Includes XT60 cable</li>
                    <li>
                      Also compatible with XT150 and Custom Fit cables (purchase
                      separately)
                    </li>
                  </ul>
                  <h4 class={style.titleAvoid}>Reasons to avoid</h4>
                  <ul>
                    <li>Slower maximum charge rate (500W)</li>
                    <li>Doesn't include XT150 cable (purchase separately)</li>
                  </ul>

                  <ButtonList>
                    <Button
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/ecoflow-500w-alternator-charger?variant=54348224954441"
                      icon="EcoFlow"
                    >
                      Buy from EcoFlow
                    </Button>
                    <Button
                      affiliate="https://amzn.to/4jzlwsP"
                      href="https://www.amazon.com/dp/B0FYCJ6KD3/"
                      secondary
                      icon="Amazon"
                    >
                      Buy on Amazon
                    </Button>
                    <Button
                      href={namedRoutes.ecoflow.altCharger.quick}
                      secondary
                      icon="Matanich"
                    >
                      Read My Review
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },

            // {
            //   preview: (
            //     <AccordionPreview
            //       headingAs="h3"
            //       prefix="Coming soon"
            //       heading="Alternator Charger Plus 1000"
            //       src="/images/products/ecoflow/alternator-charger-plus-1000/front.webp"
            //     />
            //   ),
            //   content: (
            //     <Fragment>
            //       <p>
            //         Second generation alternator charger. This will be EcoFlow’s
            //         fastest alternator charger yet, with 1000W output and 300W
            //         solar input.{" "}
            //         <Link href={namedRoutes.ecoflow.altCharger.gen2}>
            //           Here is what we know so far
            //         </Link>
            //         , more details coming soon.
            //       </p>

            //       <ReviewItem href={namedRoutes.ecoflow.altCharger.gen2} />

            //       <h4 class={style.titleBuy}>Reasons to buy</h4>
            //       <ul>
            //         <li>300W solar input</li>
            //         <li>Includes XT150 cable</li>
            //         <li>
            //           Fast charge rate when using XT150 or 4+8 output cable
            //           (1000W)
            //         </li>
            //         <li>
            //           Also compatible with XT60 and 4+8 output cables (purchase
            //           separately)
            //         </li>
            //         <li>
            //           Settings to lower charge rate if needed when using XT150
            //           or 4+8 output cable (100-1000W)
            //         </li>
            //       </ul>
            //       <h4 class={style.titleAvoid}>Reasons to avoid</h4>
            //       <ul>
            //         <li>Doesn't include XT60 cable (purchase separately)</li>
            //         <li>
            //           Not yet released (consider the other models if unable to
            //           wait)
            //         </li>
            //       </ul>

            //       <ButtonList>
            //         <Button
            //           href={namedRoutes.ecoflow.altCharger.gen2}
            //           icon="Matanich"
            //         >
            //           Read My Review
            //         </Button>
            //       </ButtonList>
            //     </Fragment>
            //   ),
            // },
            // {
            //   preview: (
            //     <AccordionPreview
            //       headingAs="h3"
            //       prefix="Coming soon"
            //       heading="Alternator Charger 600"
            //       src="/images/products/ecoflow/alternator-charger-600/front.webp"
            //     />
            //   ),
            //   content: (
            //     <Fragment>
            //       <p>
            //         Second generation alternator charger.{" "}
            //         <Link href={namedRoutes.ecoflow.altCharger.gen2}>
            //           Here is what we know so far
            //         </Link>
            //         , more details coming soon.
            //       </p>

            //       <ReviewItem href={namedRoutes.ecoflow.altCharger.gen2} />

            //       <h4 class={style.titleBuy}>Reasons to buy</h4>
            //       <ul>
            //         <li>Includes XT60 cable</li>
            //         <li>
            //           Also compatible with XT150 and Custom Fit cables (purchase
            //           separately)
            //         </li>
            //       </ul>
            //       <h4 class={style.titleAvoid}>Reasons to avoid</h4>
            //       <ul>
            //         <li>Slower maximum charge rate (600W)</li>
            //         <li>Doesn't include XT150 cable (purchase separately)</li>
            //         <li>
            //           Not yet released (consider the other models if unable to
            //           wait)
            //         </li>
            //       </ul>

            //       <ButtonList>
            //         <Button
            //           href={namedRoutes.ecoflow.altCharger.gen2}
            //           icon="Matanich"
            //         >
            //           Read My Review
            //         </Button>
            //       </ButtonList>
            //     </Fragment>
            //   ),
            // },
          ]}
        />

        <FixedTable>
          <table>
            <thead>
              <tr>
                <th class={editorialStyle.sticky}>Model</th>
                {/* <th>Plus 1000</th>
                <th>600</th> */}
                <th>800W</th>
                <th>500W</th>
              </tr>
            </thead>
            <tbody>
              <tr class={editorialStyle.noHover}>
                <th class={editorialStyle.sticky}>
                  <strong>Photo</strong>
                </th>
                {/* <td>
                  <Image
                    src="/images/products/ecoflow/alternator-charger-plus-1000/front.webp"
                    alt="EcoFlow Alternator Charger Plus 1000"
                    maxHeight={100}
                  />
                </td>
                <td>
                  <Image
                    src="/images/products/ecoflow/alternator-charger-600/front.webp"
                    alt="EcoFlow Alternator Charger 600"
                    maxHeight={100}
                  />
                </td> */}
                <td>
                  <Image
                    src="/images/products/ecoflow/alternator-charger-800w/front-right.webp"
                    alt="EcoFlow Alternator Charger 800W"
                    maxHeight={100}
                  />
                </td>
                <td>
                  <Image
                    src="/images/products/ecoflow/alternator-charger-500w/front.webp"
                    alt="EcoFlow Alternator Charger 500W"
                    maxHeight={100}
                  />
                </td>
              </tr>
              <tr class={editorialStyle.noHover}>
                <th class={editorialStyle.sticky}>
                  <strong>Links</strong>
                </th>
                {/* <td>
                  <ButtonBar>
                    {data[1000].link ? (
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href={data[1000].link}
                        icon="EcoFlow"
                      >
                        Store
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://au.ecoflow.com/products/ecoflow-alternator-charger-plus-1000"
                        icon="EcoFlow"
                      >
                        Australia
                      </Button>
                    )}
                    <Button
                      size="small"
                      external
                      href="https://manuals.ecoflow.com/us/product/alternator-charger-plus-1000"
                      secondary
                    >
                      Manual
                    </Button>
                  </ButtonBar>
                </td>
                <td>
                  <ButtonBar>
                    {data[600].link ? (
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href={data[600].link}
                        icon="EcoFlow"
                      >
                        Store
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        affiliateData={affiliateMerchants.ecoflow}
                        href="https://jp.ecoflow.com/products/ecoflow-alternator-charger-600"
                        icon="EcoFlow"
                      >
                        Japan
                      </Button>
                    )}
                    <Button
                      size="small"
                      external
                      href="https://manuals.ecoflow.com/us/product/alternator-charger-600"
                      secondary
                    >
                      Manual
                    </Button>
                  </ButtonBar>
                </td> */}
                <td>
                  <ButtonBar>
                    <Button
                      size="small"
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/800w-alternator-charger?variant=41064248836169"
                      icon="EcoFlow"
                    >
                      Store
                    </Button>
                    <Button
                      size="small"
                      external
                      href="https://manuals.ecoflow.com/us/product/alternator-charger-800w"
                      secondary
                    >
                      Manual
                    </Button>
                  </ButtonBar>
                </td>
                <td>
                  <ButtonBar>
                    <Button
                      size="small"
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/ecoflow-500w-alternator-charger?variant=54348224954441"
                      icon="EcoFlow"
                    >
                      Store
                    </Button>
                    <Button
                      size="small"
                      external
                      href="https://manuals.ecoflow.com/eu/product/alternator-charger-500w"
                      secondary
                    >
                      Manual
                    </Button>
                  </ButtonBar>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Price</strong>
                </th>
                {/* <td>
                  {data[1000].price
                    ? `$${data[1000].price}`
                    : "¥59,400 (~$390)"}
                </td>
                <td>
                  {data[600].price ? `$${data[600].price}` : "¥35,000 (~$230)"}
                </td> */}
                <td>
                  <NoWrap>
                    $269 <s>$599</s>
                  </NoWrap>
                </td>
                <td>
                  <NoWrap>
                    $199 <s>$389</s>
                  </NoWrap>
                </td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>Cable Included</strong> <sup>2</sup>
                </th>
                {/* <td>
                  <mark class={style.xt150}>
                    <strong>XT150</strong>
                  </mark>{" "}
                  <sup>1</sup>
                </td>
                <td>
                  <mark class={style.xt60}>
                    <strong>XT60</strong>
                  </mark>{" "}
                  <sup>1</sup>
                </td> */}
                <td>
                  <mark class={style.xt150}>
                    <strong>XT150</strong>
                  </mark>{" "}
                  <sup>1</sup>
                </td>
                <td>
                  <mark class={style.xt60}>
                    <strong>XT60</strong>
                  </mark>{" "}
                  <sup>1</sup>
                </td>
              </tr>
              <tr class={editorialStyle.rowBorder}>
                <th class={editorialStyle.sticky}>
                  <strong>Max Charge Rate</strong> <sup>3</sup>
                  <br />
                  <mark class={style.xt60}>XT60 cable</mark>
                </th>
                {/* <td>720W</td>
                <td>
                  <mark class={style.xt60}>
                    <strong>600W</strong>
                  </mark>
                </td> */}
                <td>500W</td>
                <td>
                  <mark class={style.xt60}>
                    <strong>500W</strong>
                  </mark>
                </td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Max Charge Rate</strong>
                  <br />
                  <mark class={style.xt150}>XT150 cable</mark>
                </th>
                {/* <td>
                  <mark class={style.xt150}>
                    <strong>1,000W</strong>
                  </mark>
                </td>
                <td>600W</td> */}
                <td>
                  <mark class={style.xt150}>
                    <strong>800W</strong>
                  </mark>
                </td>
                <td>500W</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Max Reverse Charge</strong> <sup>3</sup>
                  <br />
                  <mark class={style.xt150}>XT150 cable</mark>
                </th>
                {/* <td>
                  <mark class={style.xt150}>
                    <strong>1,000W</strong>
                  </mark>
                </td>
                <td>600W</td> */}
                <td>
                  <mark class={style.xt150}>
                    <strong>800W</strong>
                  </mark>
                </td>
                <td>500W</td>
              </tr>
              <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Battery Maintenance</strong> <sup>3</sup>
                  <br />
                  <mark class={style.xt150}>XT150 cable</mark>
                </th>
                {/* <td>
                  <mark class={style.xt150}>
                    <strong>100W</strong>
                  </mark>
                </td>
                <td>100W</td> */}
                <td>
                  <mark class={style.xt150}>
                    <strong>100W</strong>
                  </mark>
                </td>
                <td>100W</td>
              </tr>
              {/* <tr>
                <th class={editorialStyle.sticky}>
                  <strong>Solar Input</strong>
                </th>
                <td>
                  11-60V⎓15A
                  <br />
                  300W max
                </td>
                <td>--</td>
                <td>--</td>
                <td>--</td>
              </tr> */}
            </tbody>
          </table>
        </FixedTable>
        <p class="caption">
          <strong>1.</strong> Bolded values are defaults with included cable.{" "}
          <strong>2.</strong> Custom Fit Output Cable offers same functionality
          and charge rates as XT150 output cable. <strong>3.</strong> Reverse
          Charge and Battery Maintenance modes aren't supported while using the
          XT60 output cable and the max charge rate could be lower depending on
          the power station.
        </p>

        <SlugHeading tag="h3">
          Cable Options for Alternator Chargers
        </SlugHeading>
        <p>
          Each charger includes a cable, but can be swapped out to better match
          your system.
          {/* Here is an overview of the Gen 1 cables. See info on{" "}
          <Link href={`${namedRoutes.ecoflow.altCharger.gen2}#output-cables`}>
            Gen 2 cables
          </Link>
          . */}
        </p>
        <Accordion
          right
          items={[
            {
              preview: (
                <AccordionPreview
                  headingAs="h4"
                  prefix="Full features"
                  heading="XT150 Output Cable"
                  src="/images/products/ecoflow/alternator-charger-800w/output-cable-xt150-2m.webp"
                />
              ),
              content: (
                <Fragment>
                  <p>
                    Enables full charge rate and advanced features on compatible
                    power stations.
                  </p>
                  <h4 class={style.titleBuy}>Reasons to buy</h4>
                  <ul>
                    <li>Compatible with both 800W and 500W models</li>
                    <li>
                      Unlocks more features on 500W model (reverse charge,
                      battery maintenance)
                    </li>
                    <li>Unlocks full charge rate</li>
                    <li>Variable charge rate (100W to max charge rate)</li>
                    <li>
                      XT60 ports are already used (uses XT150 extra battery
                      port)
                    </li>
                  </ul>
                  <h4 class={style.titleAvoid}>Reasons to avoid</h4>
                  <ul>
                    <li>Not compatible with your power station</li>
                    <li>May require Smart Generator Adapter</li>
                  </ul>

                  <ButtonList>
                    <Button
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/aecoflow-alternator-charger-xt150-output-cable-2m"
                      icon="EcoFlow"
                    >
                      Buy from EcoFlow
                    </Button>
                    {/* <Button affiliate="" href="" secondary icon="Amazon">
                      Buy on Amazon
                    </Button> */}
                    <Button
                      secondary
                      external
                      href={namedRoutes.ecoflow.altCharger.quick}
                      icon="Matanich"
                    >
                      Read My Review
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
            {
              preview: (
                <AccordionPreview
                  headingAs="h4"
                  prefix="Full features / Avoids adapter"
                  heading="Custom Fit Output Cable"
                  src="/images/products/ecoflow/accessories/output-cable-custom-fit-2m.webp"
                />
              ),
              content: (
                <Fragment>
                  <p>
                    Avoids the Smart Generator Adapter while enabling full
                    charge rate and advanced features on compatible power
                    stations.
                  </p>
                  <p>
                    Enables adapter free full charge rate and advanced features
                    for specific power stations.
                  </p>
                  <h4 class={style.titleBuy}>Reasons to buy</h4>
                  <ul>
                    <li>Avoids using Smart Generator Adapter</li>
                    <li>Compatible with both 800W and 500W models</li>
                    <li>
                      Unlocks more features on 500W model (reverse charge,
                      battery maintenance)
                    </li>
                    <li>Unlocks full charge rate</li>
                    <li>Variable charge rate (100W to max charge rate)</li>
                    <li>
                      XT60 ports are already used (uses smart extra battery
                      port)
                    </li>
                  </ul>
                  <h4 class={style.titleAvoid}>Reasons to avoid</h4>
                  <ul>
                    <li>Not compatible with your power station</li>
                  </ul>

                  <ButtonList>
                    <Button
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/ecoflow-alternator-charger-custom-fit-output-cable-2m"
                      icon="EcoFlow"
                    >
                      Buy from EcoFlow
                    </Button>
                    {/* <Button affiliate="" href="" secondary icon="Amazon">
                      Buy on Amazon
                    </Button> */}
                  </ButtonList>
                </Fragment>
              ),
            },
            {
              preview: (
                <AccordionPreview
                  headingAs="h4"
                  prefix="Limited features / Max compatibility"
                  heading="XT60 Output Cable"
                  src="/images/products/ecoflow/alternator-charger-500w/output-cable-xt60i.webp"
                />
              ),
              content: (
                <Fragment>
                  <p>
                    Compatible with most power stations, but limited charge rate
                    and no reverse charging.
                  </p>
                  <h4 class={style.titleBuy}>Reasons to buy</h4>
                  <ul>
                    <li>
                      Compatible with more power stations (even some 3rd party)
                    </li>
                    <li>
                      Extra battery port is already used (uses XT60 instead)
                    </li>
                  </ul>
                  <h4 class={style.titleAvoid}>Reasons to avoid</h4>
                  <ul>
                    <li>
                      Slower maximum charge rate (500W, even on 800W model)
                    </li>
                    <li>No reverse charge mode</li>
                    <li>No battery maintenance mode</li>
                    <li>Fixed charge rate (500W max)</li>
                    <li>
                      Alternator Charger auto shuts off after 30s of not
                      charging (this can be frustrating)
                    </li>
                  </ul>

                  <ButtonList>
                    <Button
                      affiliateData={affiliateMerchants.ecoflow}
                      href="https://us.ecoflow.com/products/ecoflow-alternator-charger-xt60-output-cable-1m"
                      icon="EcoFlow"
                    >
                      Buy from EcoFlow
                    </Button>
                    {/* <Button affiliate="" href="" secondary icon="Amazon">
                      Buy on Amazon
                    </Button> */}
                    <Button
                      secondary
                      external
                      href={namedRoutes.ecoflow.altCharger.quick}
                      icon="Matanich"
                    >
                      Read My Review
                    </Button>
                  </ButtonList>
                </Fragment>
              ),
            },
          ]}
        />

        <SlugHeading tag="h3">Accessories for Alternator Chargers</SlugHeading>
        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/delta-pro-to-smart-generator-adapter?variant=39704596152393"
              img="/images/products/ecoflow/accessories/smart-generator-adapter.webp"
              title="Smart Generator Adapter"
              description={
                <Fragment>
                  Required by some power stations when using XT150 output cable
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ecoflow-alternator-charger-extended-fuse-cable-2m"
              img="/images/products/ecoflow/accessories/extended-fuse-cable-2m.webp"
              title="Extended Fuse Cable"
              description={<Fragment>Length: 2m</Fragment>}
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
        </ul>

        <h3>Learn More</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.review} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.transit} />
          </li>
          {/* <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.quick} />
          </li> */}

          <li>
            <ReviewItem href={namedRoutes.ecoflow.altCharger.gen2} />
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">Expansion Batteries</SlugHeading>
        <p>
          Adding extra batteries is the easiest way to double or triple your
          off-grid runtime without swapping out your entire power station or
          rewiring your build. For a complete breakdown of compatibility, cable
          requirements, adapter needs, and maximum expansion limits for every
          model, check out my{" "}
          <Link href={namedRoutes.ecoflow.batteryCompatibility}>
            battery compatibility guide
          </Link>
          :
        </p>

        <ReviewItem href={namedRoutes.ecoflow.batteryCompatibility} />
      </Center>
      <Center>
        <SlugHeading tag="h2">Solar Power</SlugHeading>
        <p>
          Solar charging offers a quiet, sustainable, and free way to extend
          off-grid time. However, its effectiveness depends entirely on
          sunlight; factors like shade, sun angle, temperature, and wiring
          configuration (series, parallel, or series-parallel) all significantly
          impact power output.
        </p>

        <SlugHeading tag="h3">Solar Array Configuration</SlugHeading>
        <p>
          Solar panel setups often involve arrays of multiple panels using
          series, parallel, or series-parallel wiring. Series connections add
          voltage but can suffer significant power drops if even one panel is
          partially shaded. Parallel wiring adds current (amperage), allowing
          unshaded panels to still produce power, ideal for patchy sunlight.
          Series-parallel setups combine both approaches.
        </p>
        <Accordion
          headingsAs="h4"
          items={[
            {
              heading: "Calculating Solar Array Output",
              content: (
                <Fragment>
                  <p>
                    To calculate the total voltage and amperage of your solar
                    array, you'll use the Open Circuit Voltage (Voc) and Short
                    Circuit Current (Isc) listed on your panels.
                  </p>
                  <p>For panels wired in series:</p>
                  <p style="padding:0.6em 1em; background: #ddd; border-radius: 6px;">
                    Total Array Voltage = Voc_panel1 + Voc_panel2 + ...
                    <br />
                    Total Array Amperage = Isc_of_a_single_panel
                  </p>
                  <p>For panels wired in parallel:</p>
                  <p style="padding:0.6em 1em; background: #ddd; border-radius: 6px;">
                    Total Array Voltage = Voc_of_a_single_panel
                    <br />
                    Total Array Amperage = Isc_panel1 + Isc_panel2 + ...
                  </p>
                  <p>
                    In a series-parallel setup, you'll first calculate the
                    voltage of each series string, then add the current of those
                    strings together if they're connected in parallel.
                  </p>
                </Fragment>
              ),
            },
          ]}
        />

        <SlugHeading tag="h3">Solar Panel Types</SlugHeading>
        <p>
          EcoFlow offers both{" "}
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/collections/mountable-solar-panels"
          >
            mountable
          </Link>{" "}
          and{" "}
          <Link
            affiliateData={affiliateMerchants.ecoflow}
            href="https://us.ecoflow.com/collections/portable-solar-panels"
          >
            portable
          </Link>{" "}
          solar panels, some of which are rigid, flexible, traditional, or
          bifacial.
        </p>

        <Accordion
          headingsAs="h4"
          items={[
            {
              heading: "Types of Solar Panels",
              content: (
                <Fragment>
                  <p>
                    <strong>Mountable vs portable:</strong> Mountable solar
                    panels provide a semi-permanent and typically higher-output
                    charging solution affixed to the roof, whereas portable
                    solar panels offer the flexibility to chase the sun and can
                    be stowed away when not in use or during travel.
                  </p>
                  <p>
                    <strong>Rigid vs flexible:</strong> Rigid solar panels, with
                    their thicker framed design, offer greater durability and
                    efficiency and are common for roof installations. Flexible
                    panels are much thinner, lighter‑weight, and can conform to
                    a curved surface for stealthier installations.
                  </p>
                  <p>
                    <strong>Bifacial vs monofacial:</strong> Bifacial solar
                    panels gather light from both sides for potentially higher
                    energy yield than traditional monofacial panels.
                  </p>
                  <p>
                    <strong>Monocrystalline vs polycrystalline:</strong>{" "}
                    Monocrystalline panels are more efficient and therefore
                    space-saving, ideal for limited RV/van roof space, while
                    older polycrystalline panels can be less expensive but
                    aren’t recommended.
                  </p>
                </Fragment>
              ),
            },
          ]}
        />

        <p>
          While EcoFlow provides various solar panel options and bundles,
          prioritize panels that physically fit your vehicle; using other
          reputable brands is okay. I personally use{" "}
          <Link external href="https://www.newpowa.com/">
            Newpowa
          </Link>{" "}
          panels as they fit my rack setup best.
        </p>

        <SlugHeading tag="h3">Accessories for Solar Panels</SlugHeading>
        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/collections/delta-3-series-accessories/products/solar-charging-cable?variant=39880496382025"
              img="/images/products/ecoflow/accessories/solar-to-xt60-xt60i-charging-cable.webp"
              title="Solar to XT60/XT60i Charging Cable"
              description={<Fragment>Charge from solar panels</Fragment>}
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/solar-extension-cable?_pos=1&_sid=e350c6fc3&_ss=r&variant=31865877823561"
              img="/images/products/ecoflow/accessories/solar-extension-cable.webp"
              title="Solar Extension Cable"
              description={
                <Fragment>Provides additional cable length</Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/solar-parallel-connection-cable?variant=31289813729353"
              img="/images/products/ecoflow/accessories/solar-parallel-connection-cable.webp"
              title="Solar Parallel Connection Cable"
              description={
                <Fragment>Connects solar panels in parallel</Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">Shore Power</SlugHeading>
        <p>
          Shore power charging allows you to replenish your EcoFlow power
          station by plugging into a standard electrical outlet, often found at
          campgrounds or homes, using the power station's normal AC charging
          cable. For easier charging, you can install a weather-resistant AC
          port on your vehicle, such as the{" "}
          <Link
            affiliate="https://amzn.to/4juX4cb"
            href="https://www.amazon.com/dp/B009ANV81S"
          >
            Noco GCP1
          </Link>
          .
        </p>

        <SlugHeading tag="h3">EV Charging Station</SlugHeading>
        <p>
          For enhanced charging flexibility, the Delta Pro and Delta Pro 3
          models offer the unique ability to recharge at EV charging stations
          when paired with an appropriate adapter (see below accessories).
        </p>
      </Center>
      <Center>
        <SlugHeading tag="h2">Generator Charging</SlugHeading>
        <p>
          For situations where solar or alternator charging might be
          insufficient, especially during extended cloudy periods or when
          stationary, generator charging provides a reliable and powerful backup
          to keep your EcoFlow system topped up. However, it's important to
          consider that generators are very noisy, making them unsuitable for
          use in quiet campgrounds, residential areas, or during nighttime hours
          where noise restrictions apply. While I don't have personal experience
          using a generator with a power station, EcoFlow offers several models
          designed to complement their systems.
        </p>
        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/smart-generator-4000-dual-fuel?_pos=1&_sid=38f1d1b01&_ss=r&variant=41434656637001"
              img="/images/products/ecoflow/smart-generator-4000-dual-fuel/front-right.webp"
              title="EcoFlow Smart Generator 4000 (Dual Fuel)"
              description={<Fragment>3,200W DC Output</Fragment>}
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/smart-generator-3000-dual-fuel?_pos=2&_sid=38f1d1b01&_ss=r&variant=41631793250377"
              img="/images/products/ecoflow/smart-generator-3000-dual-fuel/front-right.webp"
              title="EcoFlow Smart Generator 3000 (Dual Fuel)"
              description={<Fragment>3,000W DC Output</Fragment>}
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://www.ecoflow.com/us/dual-fuel-smart-generator"
              img="/images/products/ecoflow/smart-generator/front-left.webp"
              title="EcoFlow Smart Generator (Dual Fuel)"
              description={<Fragment>1,800W DC Output</Fragment>}
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
        </ul>
      </Center>
      <Center>
        <SlugHeading tag="h2">Accessories</SlugHeading>
        <ul class="none">
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/delta-pro-remote-control?variant=39428709351497"
              img="/images/products/ecoflow/accessories/remote-control.webp"
              title="Remote Control"
              titleSuffix="(Delta Pro, Delta Pro 3)"
              description={<Fragment>Remote monitoring and control</Fragment>}
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/delta-pro-to-smart-generator-adapter?variant=39704596152393"
              img="/images/products/ecoflow/accessories/smart-generator-adapter.webp"
              title="Smart Generator Adapter"
              description={
                <Fragment>
                  Required by some power stations when using XT150 output cable
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ecoflow-extra-battery-expansion-adapter?variant=55507615645769"
              img="/images/products/ecoflow/accessories/extra-battery-expansion-adapter.webp"
              title="Extra Battery Expansion Adapter"
              description={
                <Fragment>
                  Connects two extra batteries for the DELTA 3 Max Plus and
                  Ultra Plus
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/collections/delta-3-series-accessories/products/solar-charging-cable?variant=39880496382025"
              img="/images/products/ecoflow/accessories/solar-to-xt60-xt60i-charging-cable.webp"
              title="Solar to XT60/XT60i Charging Cable"
              description={<Fragment>Charge from solar panels</Fragment>}
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/solar-extension-cable?_pos=1&_sid=e350c6fc3&_ss=r&variant=31865877823561"
              img="/images/products/ecoflow/accessories/solar-extension-cable.webp"
              title="Solar Extension Cable"
              description={
                <Fragment>Provides additional cable length</Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/solar-parallel-connection-cable?variant=31289813729353"
              img="/images/products/ecoflow/accessories/solar-parallel-connection-cable.webp"
              title="Solar Parallel Connection Cable"
              description={
                <Fragment>Connects solar panels in parallel</Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/ev-x-stream-adapter-delta-pro-ultra?variant=40758561964105"
              img="/images/products/ecoflow/accessories/ev-x-stream-adapter-delta-pro-ultra.webp"
              title="EV X-Stream Adapter"
              titleSuffix="(Delta Pro 3, Delta Pro Ultra)"
              description={
                <Fragment>7200W charging via J1772 EV connector</Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/collections/rv-ev-power-accessories/products/ev-x-stream-adapter-delta-pro?variant=39366327599177"
              img="/images/products/ecoflow/accessories/ev-x-stream-adapter-delta-pro.webp"
              title="EV X-Stream Adapter"
              titleSuffix="(Delta Pro)"
              description={
                <Fragment>3400W charging via J1772 EV connector</Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/products/car-charging-cable?variant=39308519604297"
              img="/images/products/ecoflow/accessories/car-charging-cable.webp"
              title="Car Charging Cable"
              description={
                <Fragment>
                  Charge from your vehicle's cigarette lighter socket
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
          <li>
            <PhotoListItem
              affiliateData={affiliateMerchants.ecoflow}
              href="https://us.ecoflow.com/collections/rv-ev-power-accessories/products/portable-power-station-grounding-adapter?variant=39987909984329"
              img="/images/products/ecoflow/accessories/portable-power-station-grounding-adapter.webp"
              title="Portable Power Station Grounding Adapter"
              description={
                <Fragment>
                  Neutral-ground bonding prevents floating ground
                </Fragment>
              }
              buttonText="EcoFlow"
              buttonIcon="EcoFlow"
            />
          </li>
        </ul>

        <Tags tags={headerInfo.tags} brands={headerInfo.brands} />
      </Center>
      <Center>
        <h3>Related</h3>
        <ul class="none">
          <li>
            <ReviewItem href={namedRoutes.bluetti.vanlife} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.alternatorChargers} />
          </li>
          <li>
            <ReviewItem href={namedRoutes.inventory.vanlife} />
          </li>
        </ul>
      </Center>
      <Questions />
      <Center>
        <Author
          author
          published={headerInfo.datePublished}
          updated={headerInfo.dateModified}
        />
      </Center>
    </Editorial>
  </div>
);

export default Page;
